// eslint-disable-next-line import/no-unresolved
import { registerSW } from 'virtual:pwa-register';
import { store } from '../createStore';
import {
  connectionVerified,
  setOnline,
  setOffline,
} from '../store/connection/actions';
import { setConnectivityCheckHandler } from './connectivityChecks';
import { checkNetworkConnectivity } from './networkConnectivity';

const CONNECTION_CHECK_INTERVAL_MS = 5000;
const CONNECTION_CHECK_TIMEOUT_MS = 3000;
const OFFLINE_FAILURE_THRESHOLD = 3;
// Spaces out requested checks so a burst of failed requests during a brief
// blip cannot reach the offline threshold within a second.
const REQUESTED_CHECK_MIN_SPACING_MS = 2000;

let cleanupNetworkMonitoring: (() => void) | undefined;

async function canReachBackend(): Promise<boolean> {
  return await checkNetworkConnectivity({
    timeoutMs: CONNECTION_CHECK_TIMEOUT_MS,
  });
}

/**
 * Initializes network monitoring for the application.
 * Temporarily relies on polling instead of browser online/offline events.
 * Browser events can report a stale or environment-specific network state, so
 * connection state is based on whether the app can reach auth.
 */
export function initializeNetworkMonitoring() {
  cleanupNetworkMonitoring?.();

  const cleanupCallbacks: Array<() => void> = [];
  let pollingIntervalId: number | undefined;
  let checkInFlight = false;
  let consecutiveFailures = 0;
  let lastCheckStartedAt = 0;
  let verificationRequested = false;
  let requestedCheckTimeoutId: number | undefined;

  const stopPolling = () => {
    if (pollingIntervalId === undefined) {
      return;
    }

    window.clearInterval(pollingIntervalId);
    pollingIntervalId = undefined;
  };

  const checkConnection = async () => {
    if (checkInFlight) {
      return;
    }

    checkInFlight = true;
    lastCheckStartedAt = Date.now();

    try {
      const isReachable = await canReachBackend();
      const isOnline = store.getState().connectionState.isOnline;

      if (isReachable) {
        consecutiveFailures = 0;
        // A request stays pending until a check succeeds, so a failed requested
        // check followed by a successful poll still retries queued saves.
        const verifying = verificationRequested;
        verificationRequested = false;
        if (!isOnline) {
          store.dispatch(setOnline());
        } else if (verifying) {
          store.dispatch(connectionVerified());
        }
      } else {
        consecutiveFailures += 1;
      }
      if (
        !isReachable &&
        isOnline &&
        consecutiveFailures >= OFFLINE_FAILURE_THRESHOLD
      ) {
        store.dispatch(setOffline());
      }
    } finally {
      checkInFlight = false;
    }
  };

  const startPolling = () => {
    if (pollingIntervalId !== undefined) {
      return;
    }

    void checkConnection();
    pollingIntervalId = window.setInterval(
      checkConnection,
      CONNECTION_CHECK_INTERVAL_MS,
    );
  };

  // Register service worker for PWA
  if ('serviceWorker' in navigator) {
    registerSW({
      immediate: true,
    });
  }

  const requestCheck = () => {
    verificationRequested = true;
    if (requestedCheckTimeoutId !== undefined) {
      return;
    }

    const delay = Math.max(
      0,
      lastCheckStartedAt + REQUESTED_CHECK_MIN_SPACING_MS - Date.now(),
    );
    requestedCheckTimeoutId = window.setTimeout(() => {
      requestedCheckTimeoutId = undefined;
      void checkConnection();
    }, delay);
  };

  cleanupCallbacks.push(setConnectivityCheckHandler(requestCheck));
  cleanupCallbacks.push(() => window.clearTimeout(requestedCheckTimeoutId));

  startPolling();

  const cleanup = () => {
    stopPolling();
    cleanupCallbacks.forEach((cleanup) => cleanup());
    if (cleanupNetworkMonitoring === cleanup) {
      cleanupNetworkMonitoring = undefined;
    }
  };

  cleanupNetworkMonitoring = cleanup;

  return cleanup;
}
